import * as React from 'react'
import { ChangeEvent } from 'react'

import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import { useTheme } from '@mui/material/styles'

import type { FieldOption } from './fieldRenderers/typings'
import { getOptionsStyles } from './styling'

type Props = {
  onChangeOption: (event: ChangeEvent<HTMLInputElement>, value: string) => void
  value: string | number
  options: FieldOption[]
  title?: string
  marginLeft?: number
  footer?: string
  isTidalThemeActive: boolean
  inlineRadioButtonsOnly?: boolean
}

/**
 * Radio group for choosing a field's display option.
 *
 * Presentational: the active theme arrives as a prop so the component does not
 * read from the store, and its two theme-dependent indents come from
 * `getOptionsStyles`.
 */
export function SettingCardOptions({
  onChangeOption,
  value,
  options,
  title = 'Choose display option',
  marginLeft,
  footer,
  isTidalThemeActive,
  inlineRadioButtonsOnly = false,
}: Props) {
  const theme = useTheme()
  const styles = getOptionsStyles(theme, isTidalThemeActive)

  if (inlineRadioButtonsOnly) {
    return (
      <Stack sx={styles.radioRow}>
        <RadioGroup
          onChange={onChangeOption}
          value={value}
          sx={{ display: 'flex', flexDirection: 'row' }}
        >
          {options.map(({ label, value }) => (
            <FormControlLabel
              key={value}
              sx={{ mb: 0, ml: 0 }}
              label={label}
              value={value}
              control={<Radio />}
            />
          ))}
        </RadioGroup>
      </Stack>
    )
  }

  return (
    <Stack direction="row" sx={styles.outerRow}>
      <Stack sx={styles.getInnerSx(marginLeft)}>
        <Typography
          variant="body2"
          color={theme.palette.text.primary}
          letterSpacing={0.15}
          mb={1}
        >
          {title}
        </Typography>

        <RadioGroup onChange={onChangeOption} value={value}>
          {options.map(({ label, value }) => (
            <FormControlLabel
              key={value}
              sx={{ mb: 0, ml: 0 }}
              label={label}
              value={value}
              control={<Radio />}
            />
          ))}
        </RadioGroup>

        {footer && (
          <Typography
            variant="caption"
            color={theme.palette.text.primary}
            letterSpacing={0.15}
            sx={{ marginTop: '3px' }}
          >
            {footer}
          </Typography>
        )}
      </Stack>
    </Stack>
  )
}

SettingCardOptions.displayName = 'SettingCardOptions'
